'use client';
import { Box, Drawer, Grid, IconButton, Stack, useMediaQuery } from '@mui/material';
import { COLORS, Logo, MaterialIcon, Transition } from '@repo/ui';
import { getClientCookie, setClientCookie } from '@repo/utils';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import React, { ComponentPropsWithRef, useEffect, useRef, useState } from 'react';

/**
 * Renders the main layout of the app, with a side Menu and main content.
 *
 * @param {React.ReactNode} children - The content to be rendered within the layout.
 * @param {React.ReactElement} menu - The menu to be displayed within the drawer.
 * @return {React.JSX.Element} The main layout of the app.
 */
export const MenuLayout = ({
  children,
  menu,
}: {
  children: React.ReactNode;
  menu: React.ReactElement;
}): React.JSX.Element => {
  const collapsedMenuCookie = getClientCookie('collapsedMenu');
  const [collapsedMenu, setCollapsedMenu] = useState(collapsedMenuCookie === 'true' ? true : false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const isDesktop = useMediaQuery('(min-width: 1200px)', { noSsr: true });
  const [menuWithProps, setMenuWithProps] = useState<React.JSX.Element>(menu);
  const menuToggleRef = useRef<HTMLButtonElement>(null);
  const pathname = usePathname();

  useEffect(() => {
    setOpenDrawer(false);
  }, [pathname]);

  useEffect(() => {
    setMenuWithProps(
      React.cloneElement(menu, {
        closeMenu: () => setOpenDrawer(false),
        collapsedMenu,
        setCollapsedMenu: (newState: boolean) => {
          (setCollapsedMenu(newState), setClientCookie('collapsedMenu', newState.toString()));
        },
      } as ComponentPropsWithRef<'div'>),
    );
  }, [menu, setOpenDrawer, collapsedMenu, setCollapsedMenu]);

  return (
    <Grid
      component={'main'}
      data-testid="menu-layout"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100dvh',
        height: '100%',
        background: '#F3ECFF',
      }}
    >
      <Stack
        component="header"
        sx={{
          display: {
            xs: 'flex',
            lg: 'none',
          },
          height: '3rem',
          width: '100%',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          background: '#fff',
        }}
      >
        <IconButton data-testid="menu-layout-toggle" ref={menuToggleRef} onClick={() => setOpenDrawer(!openDrawer)}>
          <MaterialIcon icon="menu" color="secondary" />
        </IconButton>
        <Box component={Link} href={'/'} sx={{ display: 'flex', alignItems: 'center' }}>
          <Logo height={22} />
        </Box>
      </Stack>
      <Grid sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', height: '100%' }}>
        <Transition type="fadeLeft">
          <Drawer
            data-testid="menu-layout-drawer"
            PaperProps={{
              'data-testid': 'menu-layout-drawer-paper',
              'aria-hidden': openDrawer || isDesktop ? 'false' : 'true',
              sx: { borderRadius: 0, borderRightColor: COLORS.background[1] },
            }}
            sx={{
              width: { xs: 0, lg: collapsedMenu && isDesktop ? 90 : 280 },
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                transition: isDesktop ? 'all 0.3s cubic-bezier(0, 0, 0.2, 1) !important' : 'unset',
                overflow: 'visible',
                width: collapsedMenu && isDesktop ? 90 : 280,
                // height: { xs: 'calc(100dvh - 3rem)', lg: '100dvh' },
                minHeight: '100dvh',
                boxSizing: 'border-box',
                // mt: { xs: '3rem', lg: 0 },
              },
            }}
            variant={isDesktop ? 'persistent' : 'temporary'}
            anchor="left"
            open={isDesktop ? true : openDrawer}
            onClose={() => {
              setOpenDrawer(false);
              menuToggleRef?.current?.focus();
            }}
          >
            {menuWithProps}
          </Drawer>
        </Transition>
        <Grid sx={{ display: 'flex', minHeight: { xs: 'calc(100dvh - 3rem)', lg: '100dvh' } }} flex={1}>
          {children}
        </Grid>
      </Grid>
    </Grid>
  );
};
